import React, { useContext, useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Typography, TextField, Grid, Snackbar } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import MuiAlert from '@material-ui/lab/Alert';
import KeyboardReturnIcon from '@material-ui/icons/KeyboardReturn';
import HistoryIcon from '@material-ui/icons/History';
import { Typeahead } from 'react-bootstrap-typeahead';
import MaterialTable, { MTableToolbar } from 'material-table';
import { GlobalTheme } from '../../layouts/theme';
import { Theme } from '../contexts/themeContext.js';
import { HttpService } from '../../apiAuthorization/httpService';
import 'date-fns';
import moment from 'moment';
import DateFnsUtils from '@date-io/date-fns';
import { MuiPickersUtilsProvider, KeyboardDateTimePicker } from '@material-ui/pickers';
import { ConvertToCurrentTimeZone } from '../header/timeFormatter';

function Alert(props) {
    return <MuiAlert elevation={6} variant="filled" {...props} />;
};

const useStyles = makeStyles(theme => ({
    mb5: {
        marginBottom: "5px"
    }
}));

const TransferLocationAdd = forwardRef((props, ref) => {
    const classes = useStyles();
    const { theme } = useContext(Theme);
    const { patientSittingDto, close, onDisableWithStyle, onHideTransferLocationConfirm, defaultSite } = props;
    const [openError, setOpenError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [showLocationHistory, setShowLocationHistory] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState([]);
    const [locationDto, setLocationDto] = useState({
        LocationId:null,
        LocationPath: null,
        Bed: null,
        TransferDate: ConvertToCurrentTimeZone(new Date(), "MM/DD/YYYY hh:mm a", null, defaultSite),
    });
    const [locationHistorys, setLocationHistorys] = useState({
        columns: [
            { field: 'LocationPath', title: 'Location Path', width: '60%' },
            { field: 'Bed', title: 'Bed', width: '20%' },
            { field: 'TransferDateTimeString', title: 'Date', width: '20%' }
        ],
        data: []
    });
    const currentTheme = theme ? (GlobalTheme[theme]) : (GlobalTheme[localStorage.getItem("theme")]);

    const rowStyle = (rowData, rowIndex) => {
        return { backgroundColor: rowIndex % 2 === 0 ? '' : currentTheme.alternateGridRow };
    };

    const closeErrorSnackbar = () => {
        setOpenError(false);
    };

    const getShortName = (name) => {
        if (name.indexOf("\\") === -1)
            return name;
        else
            return ".." + name.substring(name.indexOf("\\"));
    };

    const locationChanged = (e) => {
        setSelectedLocation(e);

        if (e.length > 0) {
            setLocationDto({ ...locationDto, LocationId: e[0]?.GeoLocationId, LocationPath: e[0].GeoLocationBranchName });
        }
        else {
            setLocationDto({ ...locationDto, LocationId: null, LocationPath: null });
        }
    };

    const transferDateChanged = (date) => {
        date = moment(date).format("MM/DD/YYYY hh:mm a");
        setLocationDto({ ...locationDto, TransferDate: date });
    };

    const saveLocationHistory = () => {
        if (moment(locationDto.TransferDate) > moment(ConvertToCurrentTimeZone(new Date(), "MM/DD/YYYY hh:mm a", null, defaultSite))) {
            setErrorMessage("The Transfer Date cannot be after current time.");
            setOpenError(true);
            return;
        }

        if (patientSittingDto.LocationId === locationDto.LocationId) {
            setErrorMessage("Selected Location is same as current Location. Plese verify.");
            setOpenError(true);
            return;
        }

        onDisableWithStyle(true);
        HttpService.post('/PatientSitting/SavePOSLocationHistory', locationDto)
            .then((result) => {
                close();
                props.saveLocationTransfer(locationDto.LocationId, locationDto.LocationPath, locationDto.Bed);
            },
                (error) => {
                    //This is intentional
                }
            );

    };

    const getLocationHistorys = () => {
        HttpService.post('/PatientSitting/GetPOSLocationHistory', { AppPatientSittingId: patientSittingDto.Id })
            .then((result) => {
                setLocationHistorys({ ...locationHistorys, data: result.data });
            },
                (error) => {
                    //This is intentional
                });
    };

    useImperativeHandle(ref, () => {
        return {
            save: saveLocationHistory,
        };
    });

    useEffect(() => {
        getLocationHistorys();
        setLocationDto({ ...locationDto, AppPatientSittingId: patientSittingDto.Id, LocationId: null, LocationPath: null, Bed: null, IsDeleted: false });
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (locationDto.LocationId === null || locationDto.Bed === null || locationDto.Bed === "") {
            onDisableWithStyle(true);
        }
        else {
            onDisableWithStyle(false);
        }
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locationDto]);

    return (
        <div>
            <Snackbar open={openError} autoHideDuration={3000} onClose={closeErrorSnackbar} style={{ height: "100%" }}>
                <Alert onClose={closeErrorSnackbar} severity="error">
                    {errorMessage}
                </Alert>
            </Snackbar>
            {
                locationHistorys.data.length > 0
                &&
                <Grid container
                    direction="row"
                    justify="flex-end"
                    alignItems="flex-start"
                    className={classes.mb5}
                    spacing={2}
                >
                    <Grid item>
                        {!showLocationHistory && <HistoryIcon fontSize="large" style={{ color: currentTheme.secondary, cursor: 'pointer' }} onClick={() => { setShowLocationHistory(true); onHideTransferLocationConfirm(true); }} />}
                        {showLocationHistory && <KeyboardReturnIcon fontSize="large" style={{ color: currentTheme.secondary, cursor: 'pointer' }} onClick={() => { setShowLocationHistory(false); onHideTransferLocationConfirm(false); }} />}
                    </Grid>
                </Grid>
            }

            {
                !showLocationHistory
                &&
                <>
                    <Grid container direction="row" alignItems="center" spacing={1}>
                        <Grid item xs={12} sm={12}>
                            <Typography variant="subtitle2" gutterBottom>
                                Location <span style={{ color: 'red' }}>*</span>
                            </Typography>
                            <Typeahead
                                id="TransferLocation"
                                labelKey="GeoLocationBranchName"
                                placeholder="Choose a Location..."
                                options={props.locations}
                                selected={selectedLocation}
                                onChange={(e) => locationChanged(e)}
                                renderMenuItemChildren={(option, key) => (
                                    <>
                                        <div className="dropdown-widget">
                                            <div className="left">{getShortName(option.GeoLocationBranchName)}</div>
                                        </div>
                                    </>
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12}>
                            <Typography variant="subtitle2" gutterBottom>
                                Bed <span style={{ color: 'red' }}>*</span>
                            </Typography>
                            <TextField size="small" fullWidth
                                variant="outlined"
                                value={locationDto.Bed || ""}
                                onChange={(e) => { setLocationDto({ ...locationDto, Bed: e.target.value.trimStart() }); }}
                                InputLabelProps={{
                                    shrink: true
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12}>
                            <Typography variant="subtitle2" gutterBottom>
                                Transfer Date <span style={{ color: 'red' }}>*</span>
                            </Typography>
                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                <KeyboardDateTimePicker fullWidth hideTabs autoOk
                                    variant="inline"
                                    inputVariant="outlined"
                                    id="TransferDateDateTime"
                                    format="MM/dd/yyyy hh:mm a"
                                    size="small"
                                    disableFuture="true"
                                    maxDate={locationDto.TransferDate}
                                    value={locationDto.TransferDate}
                                    onChange={date => transferDateChanged(date)}
                                    InputAdornmentProps={{ position: "start" }}
                                    PopoverProps={{
                                        anchorOrigin: { horizontal: "left", vertical: "bottom" },
                                        transformOrigin: { horizontal: "left", vertical: "top" }
                                    }}
                                />
                            </MuiPickersUtilsProvider>
                        </Grid>
                    </Grid>
                    <br /> <br /> <br /> <br /> <br /> <br /> <br /> <br /> <br /> <br /> <br /> <br />
                </>
            }

            {
                showLocationHistory
                &&
                <MaterialTable
                    title=""
                    columns={locationHistorys.columns}
                    data={locationHistorys.data}
                    components={{
                        Toolbar: (prop) => (
                            <div style={{height: "0px" }}>
                                <MTableToolbar {...prop} />
                            </div>
                        )
                    }}
                    style={{ backgroundColor: currentTheme.background, border: '1px solid white' }}
                    options={{
                        filtering: false,
                        search: false,
                        paging: false,
                        actionsColumnIndex: -1,
                        minBodyHeight: "55vh", maxBodyHeight: "55vh",
                        headerStyle: { fontWeight: 'bold', color: currentTheme.headerColor, backgroundColor: currentTheme.headerBackground },
                        actionsCellStyle: {color: currentTheme.secondary, hover: 'white'},
                        rowStyle: rowStyle
                    }}
                />
            }
        </div>
    );
});

export default TransferLocationAdd;
